/**
 * 3D scene navigation controllers. Each one binds to a MISceneView
 * (Cesium viewer) via `attachToScene(viewer)` and configures the
 * scene's camera controller / preRender event for per-frame logic.
 */

export interface NavigationControllerOptions {
  speed?: number;
  sensitivity?: number;
}

export interface OrbitControllerOptions extends NavigationControllerOptions {
  enableTilt?: boolean;
  enableZoom?: boolean;
  enableRotate?: boolean;
  minZoomDistance?: number;
  maxZoomDistance?: number;
  inertia?: boolean;
}

export interface WalkControllerOptions extends NavigationControllerOptions {
  walkSpeed?: number;
  eyeHeight?: number;
  terrainClamp?: boolean;
  collisionDetection?: boolean;
  gravity?: boolean;
  maxSlope?: number;
}

export interface FlyControllerOptions extends NavigationControllerOptions {
  flySpeed?: number;
  maxSpeed?: number;
  acceleration?: number;
}

export interface FirstPersonControllerOptions extends NavigationControllerOptions {
  eyeHeight?: number;
  moveSpeed?: number;
  lookSpeed?: number;
  terrainClamp?: boolean;
  invertY?: boolean;
}

abstract class BaseController {
  protected viewer: any = null;
  protected isActive = false;
  protected _removeListener: (() => void) | null = null;

  /** Subclasses do their per-frame setup here. */
  protected abstract _attach(): void;

  attachToScene(viewer: any): void {
    if (this.viewer) this.detach();
    this.viewer = viewer;
    this.isActive = true;
    if (viewer && viewer.scene) this._attach();
  }

  detach(): void {
    if (this._removeListener) { try { this._removeListener(); } catch {} this._removeListener = null; }
    this.viewer = null;
    this.isActive = false;
  }

  /** Backwards-compatible alias used by older callers. */
  activate(viewer: any): void { this.attachToScene(viewer); }
  deactivate(): void { this.detach(); }
  get active(): boolean { return this.isActive; }
}

export class OrbitController extends BaseController {
  private options: Required<OrbitControllerOptions>;
  private _target: [number, number, number] | null = null;
  private _distance = 1000;

  constructor(options?: OrbitControllerOptions) {
    super();
    this.options = {
      enableTilt: options?.enableTilt ?? true,
      enableZoom: options?.enableZoom ?? true,
      enableRotate: options?.enableRotate ?? true,
      minZoomDistance: options?.minZoomDistance ?? 1,
      maxZoomDistance: options?.maxZoomDistance ?? 50_000_000,
      inertia: options?.inertia ?? true,
      speed: options?.speed ?? 1,
      sensitivity: options?.sensitivity ?? 1,
    };
  }

  protected _attach(): void {
    const sc = this.viewer?.scene?.screenSpaceCameraController;
    if (!sc) return;
    sc.enableTilt = this.options.enableTilt;
    sc.enableZoom = this.options.enableZoom;
    sc.enableRotate = this.options.enableRotate;
    sc.enableTranslate = false;
    sc.enableLook = false;
    sc.minimumZoomDistance = this.options.minZoomDistance;
    sc.maximumZoomDistance = this.options.maxZoomDistance;
    sc.inertiaSpin = this.options.inertia ? 0.9 : 0;
    sc.inertiaTranslate = this.options.inertia ? 0.9 : 0;
    sc.inertiaZoom = this.options.inertia ? 0.8 : 0;
  }

  setTarget(position: [number, number, number]): void {
    this._target = position;
    const Cesium = (globalThis as any).Cesium;
    if (this.viewer && Cesium) {
      try {
        const center = Cesium.Cartesian3.fromDegrees(position[0], position[1], position[2]);
        this.viewer.camera.lookAt(
          center,
          new Cesium.HeadingPitchRange(0, -Math.PI / 4, this._distance)
        );
      } catch {}
    }
  }
  setDistance(distance: number): void {
    this._distance = distance;
    if (this._target) this.setTarget(this._target);
  }
  getDistance(): number { return this._distance; }
  get config(): OrbitControllerOptions { return { ...this.options }; }
}

export class WalkController extends BaseController {
  private options: Required<WalkControllerOptions>;

  constructor(options?: WalkControllerOptions) {
    super();
    this.options = {
      walkSpeed: options?.walkSpeed ?? 1.4,
      eyeHeight: options?.eyeHeight ?? 1.7,
      terrainClamp: options?.terrainClamp ?? true,
      collisionDetection: options?.collisionDetection ?? true,
      gravity: options?.gravity ?? true,
      maxSlope: options?.maxSlope ?? 45,
      speed: options?.speed ?? 1,
      sensitivity: options?.sensitivity ?? 1,
    };
  }

  protected _attach(): void {
    const scene = this.viewer?.scene;
    const sc = scene?.screenSpaceCameraController;
    const Cesium = (globalThis as any).Cesium;
    if (!sc || !scene) return;
    // First-person walk: look enabled, no rotate-around-globe, no translate
    sc.enableRotate = false;
    sc.enableTilt = false;
    sc.enableTranslate = false;
    sc.enableZoom = false;
    sc.enableLook = true;
    if (this.options.terrainClamp && Cesium) {
      const tick = () => {
        try {
          const cam = scene.camera;
          const carto = Cesium.Cartographic.fromCartesian(cam.position);
          const groundH = scene.globe.getHeight(carto) ?? 0;
          const desired = groundH + this.options.eyeHeight;
          if (Math.abs(carto.height - desired) > 0.01) {
            carto.height = desired;
            cam.position = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height);
          }
        } catch {}
      };
      scene.preRender.addEventListener(tick);
      this._removeListener = () => { try { scene.preRender.removeEventListener(tick); } catch {} };
    }
  }

  setEyeHeight(h: number): void { this.options.eyeHeight = h; }
  setPosition(p: [number, number, number]): void {
    const Cesium = (globalThis as any).Cesium;
    if (this.viewer && Cesium) {
      try {
        this.viewer.camera.setView({ destination: Cesium.Cartesian3.fromDegrees(p[0], p[1], p[2]) });
      } catch {}
    }
  }
  get config(): WalkControllerOptions { return { ...this.options }; }
}

export class FlyController extends BaseController {
  private options: Required<FlyControllerOptions>;

  constructor(options?: FlyControllerOptions) {
    super();
    this.options = {
      flySpeed: options?.flySpeed ?? 50,
      maxSpeed: options?.maxSpeed ?? 500,
      acceleration: options?.acceleration ?? 5,
      speed: options?.speed ?? 1,
      sensitivity: options?.sensitivity ?? 1,
    };
  }

  protected _attach(): void {
    const sc = this.viewer?.scene?.screenSpaceCameraController;
    if (!sc) return;
    sc.enableLook = true;
    sc.enableRotate = true;
    sc.enableZoom = true;
    sc.enableTilt = true;
    sc.enableTranslate = true;
  }

  setSpeed(s: number): void { this.options.flySpeed = Math.min(s, this.options.maxSpeed); }
  get config(): FlyControllerOptions { return { ...this.options }; }
}

export class FirstPersonController extends BaseController {
  private options: Required<FirstPersonControllerOptions>;

  constructor(options?: FirstPersonControllerOptions) {
    super();
    this.options = {
      eyeHeight: options?.eyeHeight ?? 1.7,
      moveSpeed: options?.moveSpeed ?? 2,
      lookSpeed: options?.lookSpeed ?? 0.5,
      terrainClamp: options?.terrainClamp ?? true,
      invertY: options?.invertY ?? false,
      speed: options?.speed ?? 1,
      sensitivity: options?.sensitivity ?? 1,
    };
  }

  protected _attach(): void {
    const scene = this.viewer?.scene;
    const sc = scene?.screenSpaceCameraController;
    if (!sc || !scene) return;
    sc.enableLook = true;
    sc.enableRotate = false;
    sc.enableTilt = false;
    sc.enableTranslate = true;
    sc.enableZoom = false;
    // Reuse WalkController's terrain clamp for eye-height pinning
    const Cesium = (globalThis as any).Cesium;
    if (this.options.terrainClamp && Cesium) {
      const tick = () => {
        try {
          const cam = scene.camera;
          const carto = Cesium.Cartographic.fromCartesian(cam.position);
          const groundH = scene.globe.getHeight(carto) ?? 0;
          const desired = groundH + this.options.eyeHeight;
          carto.height = desired;
          cam.position = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude, carto.height);
        } catch {}
      };
      scene.preRender.addEventListener(tick);
      this._removeListener = () => { try { scene.preRender.removeEventListener(tick); } catch {} };
    }
  }

  setPosition(p: [number, number, number]): void {
    const Cesium = (globalThis as any).Cesium;
    if (this.viewer && Cesium) {
      try { this.viewer.camera.setView({ destination: Cesium.Cartesian3.fromDegrees(p[0], p[1], p[2]) }); } catch {}
    }
  }
  get config(): FirstPersonControllerOptions { return { ...this.options }; }
}
